import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import CampaignIcon from "@mui/icons-material/Campaign";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  AdminPromotion,
  AdminPromotionPayload,
  PromotionStatus,
  createAdminPromotion,
  deleteAdminPromotion,
  getAdminPromotion,
  listAdminPromotions,
  updateAdminPromotion,
  updateAdminPromotionStatus,
} from "../api/adminPromotionsApi";
import PromotionProductPicker from "../components/promotions/PromotionProductPicker";
import PromotionCategoryPicker from "../components/promotions/PromotionCategoryPicker";

const emptyPayload: AdminPromotionPayload = {
  name: "",
  slug: "",
  status: "draft",
  channel: "online",
  priority: 0,
  is_stackable_with_coupons: true,
  is_stackable_with_promotions: false,
  usage_limit: null,
  usage_per_customer_limit: null,
  starts_at: null,
  ends_at: null,
  admin_notes: "",
  triggers: [
    {
      trigger_type: "spend_threshold",
      required_subtotal: 50,
      required_quantity: null,
      config: {},
    },
  ],
  rewards: [
    {
      reward_type: "cart_discount",
      discount_type: "fixed",
      discount_value: 5,
      max_discount: null,
      reward_quantity: null,
      config: {},
    },
  ],
  products: [],
  categories: [],
};

function slugify(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function isoToLocalInput(value?: string | null) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

function localInputToIso(value: string) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function idsForRole(
  rows: { role: string; product_id?: number; category_id?: number }[],
  role: string,
  key: "product_id" | "category_id"
) {
  return rows
    .filter((row) => row.role === role)
    .map((row) => Number(row[key]))
    .filter(Number.isFinite);
}

function replaceProductRole(
  current: AdminPromotionPayload["products"],
  role: "trigger" | "reward" | "eligible",
  ids: number[]
) {
  return [
    ...current.filter((row) => row.role !== role),
    ...ids.map((id) => ({ role, product_id: id })),
  ];
}

function replaceCategoryRole(
  current: AdminPromotionPayload["categories"],
  role: "trigger" | "reward" | "eligible",
  ids: number[]
) {
  return [
    ...current.filter((row) => row.role !== role),
    ...ids.map((id) => ({ role, category_id: id })),
  ];
}

export default function PromotionsPage() {
  const [promotions, setPromotions] = useState<AdminPromotion[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<AdminPromotionPayload>(emptyPayload);
  const [error, setError] = useState<string | null>(null);

  const isEditing = editingId !== null;

  const title = useMemo(() => {
    return isEditing ? "Edit Promotion" : "Create Promotion";
  }, [isEditing]);

  async function loadPromotions() {
    setLoading(true);
    setError(null);
    try {
      const rows = await listAdminPromotions();
      setPromotions(rows);
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to load promotions.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadPromotions();
  }, []);

  function openCreate() {
    setEditingId(null);
    setForm(emptyPayload);
    setDialogOpen(true);
  }

  async function openEdit(id: number) {
    setError(null);
    try {
      const promo = await getAdminPromotion(id);

      setEditingId(id);
      setForm({
        name: promo.name,
        slug: promo.slug,
        status: promo.status,
        channel: promo.channel,
        priority: promo.priority || 0,
        is_stackable_with_coupons: !!promo.is_stackable_with_coupons,
        is_stackable_with_promotions: !!promo.is_stackable_with_promotions,
        usage_limit: promo.usage_limit,
        usage_per_customer_limit: promo.usage_per_customer_limit,
        starts_at: promo.starts_at,
        ends_at: promo.ends_at,
        admin_notes: promo.admin_notes || "",
        triggers: promo.triggers?.length ? promo.triggers : emptyPayload.triggers,
        rewards: promo.rewards?.length ? promo.rewards : emptyPayload.rewards,
        products: promo.products || [],
        categories: promo.categories || [],
      });
      setDialogOpen(true);
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to load promotion.");
    }
  }

  async function savePromotion() {
    setSaving(true);
    setError(null);

    try {
      const payload: AdminPromotionPayload = {
        ...form,
        slug: form.slug || slugify(form.name),
        priority: Number(form.priority || 0),
        usage_limit: form.usage_limit === ("" as any) ? null : form.usage_limit,
        usage_per_customer_limit:
          form.usage_per_customer_limit === ("" as any)
            ? null
            : form.usage_per_customer_limit,
      };

      if (editingId) {
        await updateAdminPromotion(editingId, payload);
      } else {
        await createAdminPromotion(payload);
      }

      setDialogOpen(false);
      await loadPromotions();
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to save promotion.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: number) {
    if (!window.confirm("Delete this promotion?")) return;

    setError(null);
    try {
      await deleteAdminPromotion(id);
      await loadPromotions();
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to delete promotion.");
    }
  }

  async function handleStatusChange(id: number, status: PromotionStatus) {
    setError(null);
    try {
      await updateAdminPromotionStatus(id, status);
      await loadPromotions();
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to update status.");
    }
  }

  const trigger = form.triggers[0];
  const reward = form.rewards[0];

  return (
    <Box p={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={800}>
            Promotions
          </Typography>
          <Typography color="text.secondary">
            Automatic cart and checkout promotions.
          </Typography>
        </Box>

        <Button variant="contained" startIcon={<CampaignIcon />} onClick={openCreate}>
          New Promotion
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Stack alignItems="center" py={8}>
          <CircularProgress />
        </Stack>
      ) : (
        <Grid container spacing={2}>
          {promotions.map((promo) => (
            <Grid item xs={12} md={6} lg={4} key={promo.id}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <Stack spacing={1.5}>
                    <Stack direction="row" justifyContent="space-between" gap={1}>
                      <Box>
                        <Typography fontWeight={800}>{promo.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {promo.slug}
                        </Typography>
                      </Box>
                      <Chip
                        label={promo.status}
                        color={promo.status === "active" ? "success" : "default"}
                        size="small"
                      />
                    </Stack>

                    <Divider />

                    <Typography variant="body2">
                      Channel: <strong>{promo.channel}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Priority: <strong>{promo.priority}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Used: <strong>{promo.total_used || 0}</strong>
                    </Typography>

                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        label="Status"
                        value={promo.status}
                        onChange={(e) =>
                          handleStatusChange(
                            promo.id,
                            e.target.value as PromotionStatus
                          )
                        }
                      >
                        <MenuItem value="draft">Draft</MenuItem>
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="paused">Paused</MenuItem>
                        <MenuItem value="archived">Archived</MenuItem>
                      </Select>
                    </FormControl>

                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => openEdit(promo.id)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDelete(promo.id)}
                      >
                        Delete
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}

          {!promotions.length && (
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography fontWeight={700}>No promotions yet.</Typography>
                  <Typography color="text.secondary">
                    Create your first automatic promotion to start testing checkout rules.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{title}</DialogTitle>

        <DialogContent dividers>
          <Stack spacing={3} pt={1}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <TextField
                  label="Name"
                  value={form.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    setForm((prev) => ({
                      ...prev,
                      name,
                      slug: slugify(name),
                    }));
                  }}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Slug"
                  value={form.slug || ""}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, slug: slugify(e.target.value) }))
                  }
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={form.status}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        status: e.target.value as PromotionStatus,
                      }))
                    }
                  >
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="paused">Paused</MenuItem>
                    <MenuItem value="archived">Archived</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Channel</InputLabel>
                  <Select
                    label="Channel"
                    value={form.channel}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        channel: e.target.value as any,
                      }))
                    }
                  >
                    <MenuItem value="online">Online</MenuItem>
                    <MenuItem value="pos">POS</MenuItem>
                    <MenuItem value="both">Both</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Priority"
                  type="number"
                  value={form.priority}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      priority: Number(e.target.value || 0),
                    }))
                  }
                  fullWidth
                />
              </Grid>
            </Grid>

            <Divider />

            <Typography variant="h6" fontWeight={800}>
  Trigger / Condition
</Typography>

<Alert severity="info">
  This decides when the promo becomes available. For example:
  Buy 3 pods get 4th free = trigger type <strong>Buy X</strong>, required quantity <strong>4</strong>, trigger category <strong>Pods</strong>.
</Alert>

<Grid container spacing={2}>
  <Grid item xs={12} md={4}>
    <FormControl fullWidth>
      <InputLabel>Trigger Type</InputLabel>
      <Select
        label="Trigger Type"
        value={trigger.trigger_type}
        onChange={(e) =>
          setForm((prev) => ({
            ...prev,
            triggers: [
              {
                ...prev.triggers[0],
                trigger_type: e.target.value as any,
                required_subtotal:
                  e.target.value === "spend_threshold"
                    ? prev.triggers[0].required_subtotal || 50
                    : null,
                required_quantity:
                  e.target.value === "buy_x"
                    ? prev.triggers[0].required_quantity || 2
                    : null,
              },
            ],
          }))
        }
      >
        <MenuItem value="spend_threshold">Spend Threshold</MenuItem>
        <MenuItem value="buy_x">Buy X Quantity</MenuItem>
        <MenuItem value="cart_contains_product">Cart Contains Product</MenuItem>
        <MenuItem value="cart_contains_category">Cart Contains Category</MenuItem>
      </Select>
    </FormControl>
  </Grid>

  {trigger.trigger_type === "spend_threshold" && (
    <Grid item xs={12} md={4}>
      <TextField
        label="Minimum Cart Subtotal ($)"
        type="number"
        value={trigger.required_subtotal ?? ""}
        helperText="Example: 50 means customer must spend $50 before tax/shipping."
        onChange={(e) =>
          setForm((prev) => ({
            ...prev,
            triggers: [
              {
                ...prev.triggers[0],
                required_subtotal: e.target.value,
              },
            ],
          }))
        }
        fullWidth
      />
    </Grid>
  )}

  {trigger.trigger_type === "buy_x" && (
    <Grid item xs={12} md={4}>
      <TextField
        label="Required Quantity"
        type="number"
        value={trigger.required_quantity ?? ""}
        helperText="Example: 4 for Buy 3 get 4th free."
        onChange={(e) =>
          setForm((prev) => ({
            ...prev,
            triggers: [
              {
                ...prev.triggers[0],
                required_quantity: Number(e.target.value || 0),
              },
            ],
          }))
        }
        fullWidth
      />
    </Grid>
  )}
</Grid>

{["buy_x", "cart_contains_product"].includes(trigger.trigger_type) && (
  <PromotionProductPicker
    label="Trigger Products"
    helperText="Optional. If selected, only these products count toward the trigger."
    selectedIds={idsForRole(form.products, "trigger", "product_id")}
    onChange={(ids) =>
      setForm((prev) => ({
        ...prev,
        products: replaceProductRole(prev.products, "trigger", ids),
      }))
    }
  />
)}

{["buy_x", "cart_contains_category"].includes(trigger.trigger_type) && (
  <PromotionCategoryPicker
    label="Trigger Categories"
    helperText="Optional. If selected, only products in these categories count toward the trigger."
    selectedIds={idsForRole(form.categories, "trigger", "category_id")}
    onChange={(ids) =>
      setForm((prev) => ({
        ...prev,
        categories: replaceCategoryRole(prev.categories, "trigger", ids),
      }))
    }
  />
)}

            <Divider />

           <Typography variant="h6" fontWeight={800}>
  Reward
</Typography>

<Alert severity="info">
  This decides what the customer gets. For free products, choose the free item below.
  For “3rd item discounted” or “4th free”, use Item Discount and choose eligible products/categories.
</Alert>

<Grid container spacing={2}>
  <Grid item xs={12} md={4}>
    <FormControl fullWidth>
      <InputLabel>Reward Type</InputLabel>
      <Select
        label="Reward Type"
        value={reward.reward_type}
        onChange={(e) =>
          setForm((prev) => ({
            ...prev,
            rewards: [
              {
                ...prev.rewards[0],
                reward_type: e.target.value as any,
                discount_type:
                  e.target.value === "free_product"
                    ? null
                    : prev.rewards[0].discount_type || "fixed",
                discount_value:
                  e.target.value === "free_product"
                    ? null
                    : prev.rewards[0].discount_value || 5,
                reward_quantity: prev.rewards[0].reward_quantity || 1,
                config: {
                  ...(prev.rewards[0].config || {}),
                  repeat: (prev.rewards[0].config as any)?.repeat || "once",
                  apply_to:
                    e.target.value === "item_discount"
                      ? (prev.rewards[0].config as any)?.apply_to ||
                        "cheapest_eligible_item"
                      : undefined,
                },
              },
            ],
          }))
        }
      >
        <MenuItem value="cart_discount">Cart Discount</MenuItem>
        <MenuItem value="item_discount">Item Discount</MenuItem>
        <MenuItem value="free_product">Free Product</MenuItem>
      </Select>
    </FormControl>
  </Grid>

  {(reward.reward_type === "cart_discount" ||
    reward.reward_type === "item_discount") && (
    <>
      <Grid item xs={12} md={4}>
        <FormControl fullWidth>
          <InputLabel>Discount Type</InputLabel>
          <Select
            label="Discount Type"
            value={reward.discount_type || "fixed"}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                rewards: [
                  {
                    ...prev.rewards[0],
                    discount_type: e.target.value as any,
                  },
                ],
              }))
            }
          >
            <MenuItem value="fixed">Fixed $ Off</MenuItem>
            <MenuItem value="percentage">Percentage % Off</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} md={4}>
        <TextField
          label="Discount Value"
          type="number"
          value={reward.discount_value ?? ""}
          helperText={
            reward.discount_type === "percentage"
              ? "Example: 100 means free."
              : "Example: 5 means $5 off."
          }
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              rewards: [
                {
                  ...prev.rewards[0],
                  discount_value: e.target.value,
                },
              ],
            }))
          }
          fullWidth
        />
      </Grid>
    </>
  )}

  {(reward.reward_type === "free_product" ||
    reward.reward_type === "item_discount") && (
    <Grid item xs={12} md={4}>
      <TextField
        label="Reward Quantity"
        type="number"
        value={reward.reward_quantity ?? 1}
        helperText="Example: 1 means one item gets the reward."
        onChange={(e) =>
          setForm((prev) => ({
            ...prev,
            rewards: [
              {
                ...prev.rewards[0],
                reward_quantity: Number(e.target.value || 1),
              },
            ],
          }))
        }
        fullWidth
      />
    </Grid>
  )}

  {(reward.reward_type === "free_product" ||
    reward.reward_type === "item_discount") && (
    <Grid item xs={12} md={4}>
      <FormControl fullWidth>
        <InputLabel>Repeat</InputLabel>
        <Select
          label="Repeat"
          value={(reward.config as any)?.repeat || "once"}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              rewards: [
                {
                  ...prev.rewards[0],
                  config: {
                    ...(prev.rewards[0].config || {}),
                    repeat: e.target.value,
                  },
                },
              ],
            }))
          }
        >
          <MenuItem value="once">Once per order</MenuItem>
          <MenuItem value="every_time">Every time condition is met</MenuItem>
        </Select>
      </FormControl>
    </Grid>
  )}

  {(reward.config as any)?.repeat === "every_time" &&
    (reward.reward_type === "free_product" ||
      reward.reward_type === "item_discount") && (
      <Grid item xs={12} md={4}>
        <TextField
          label="Group Size"
          type="number"
          value={(reward.config as any)?.group_size ?? ""}
          helperText="Example: 4 for Buy 3 get 4th free."
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              rewards: [
                {
                  ...prev.rewards[0],
                  config: {
                    ...(prev.rewards[0].config || {}),
                    group_size: Number(e.target.value || 0),
                  },
                },
              ],
            }))
          }
          fullWidth
        />
      </Grid>
    )}

  {reward.reward_type === "item_discount" && (
    <Grid item xs={12} md={4}>
      <FormControl fullWidth>
        <InputLabel>Apply Discount To</InputLabel>
        <Select
          label="Apply Discount To"
          value={(reward.config as any)?.apply_to || "cheapest_eligible_item"}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              rewards: [
                {
                  ...prev.rewards[0],
                  config: {
                    ...(prev.rewards[0].config || {}),
                    apply_to: e.target.value,
                  },
                },
              ],
            }))
          }
        >
          <MenuItem value="cheapest_eligible_item">Cheapest eligible item</MenuItem>
          <MenuItem value="most_expensive_eligible_item">
            Most expensive eligible item
          </MenuItem>
          <MenuItem value="eligible_subtotal">Eligible subtotal</MenuItem>
        </Select>
      </FormControl>
    </Grid>
  )}
</Grid>

{reward.reward_type === "free_product" && (
  <PromotionProductPicker
    label="Free Product"
    helperText="Choose the product the customer gets for free, like a battery."
    selectedIds={idsForRole(form.products, "reward", "product_id")}
    onChange={(ids) =>
      setForm((prev) => ({
        ...prev,
        products: replaceProductRole(prev.products, "reward", ids),
      }))
    }
  />
)}

{reward.reward_type === "item_discount" && (
  <>
    <PromotionProductPicker
      label="Eligible Products"
      helperText="Optional. Only these products can receive the discount."
      selectedIds={idsForRole(form.products, "eligible", "product_id")}
      onChange={(ids) =>
        setForm((prev) => ({
          ...prev,
          products: replaceProductRole(prev.products, "eligible", ids),
        }))
      }
    />

    <PromotionCategoryPicker
      label="Eligible Categories"
      helperText="Optional. Only products in these categories can receive the discount."
      selectedIds={idsForRole(form.categories, "eligible", "category_id")}
      onChange={(ids) =>
        setForm((prev) => ({
          ...prev,
          categories: replaceCategoryRole(prev.categories, "eligible", ids),
        }))
      }
    />
  </>
)}

            <Divider />

            <Typography variant="h6" fontWeight={800}>
              Limits & Schedule
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Usage Limit"
                  type="number"
                  value={form.usage_limit ?? ""}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      usage_limit: e.target.value ? Number(e.target.value) : null,
                    }))
                  }
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Usage Per Customer Limit"
                  type="number"
                  value={form.usage_per_customer_limit ?? ""}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      usage_per_customer_limit: e.target.value
                        ? Number(e.target.value)
                        : null,
                    }))
                  }
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Start At"
                  type="datetime-local"
                  value={isoToLocalInput(form.starts_at)}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      starts_at: localInputToIso(e.target.value),
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="End At"
                  type="datetime-local"
                  value={isoToLocalInput(form.ends_at)}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      ends_at: localInputToIso(e.target.value),
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.is_stackable_with_coupons}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        is_stackable_with_coupons: e.target.checked,
                      }))
                    }
                  />
                }
                label="Stack with coupons"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={form.is_stackable_with_promotions}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        is_stackable_with_promotions: e.target.checked,
                      }))
                    }
                  />
                }
                label="Stack with other promos"
              />
            </Stack>

            <TextField
              label="Admin Notes"
              value={form.admin_notes || ""}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  admin_notes: e.target.value,
                }))
              }
              multiline
              minRows={2}
              fullWidth
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={savePromotion} disabled={saving}>
            {saving ? "Saving..." : "Save Promotion"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}